import { watch, type FSWatcher } from 'node:fs';
import { shouldSkipWorkspaceWalkEntry } from './walk-workspace-tree.js';

export const FILE_INDEX_WATCH_DEBOUNCE_MS = 900;

function shouldIgnoreRelative(rel: string): boolean {
  const n = rel.replace(/\\/g, '/');
  return n.split('/').some((segment) => shouldSkipWorkspaceWalkEntry(segment));
}

function attachWatchErrorLog(w: FSWatcher): void {
  w.on('error', (err) => {
    console.error('[file-index] File watcher error:', err);
  });
}

/**
 * Prefer recursive watch (one handle). Falls back to non-recursive on older Linux (before Node 19.1).
 */
export function createFileIndexFsWatcher(resolved: string, schedule: (relPath: string | null) => void): FSWatcher {
  const onEvent = (_event: string, filename: string | Buffer | null): void => {
    if (filename != null) {
      const rel =
        typeof filename === 'string' ? filename.replace(/\\/g, '/') : filename.toString('utf8').replace(/\\/g, '/');
      if (shouldIgnoreRelative(rel)) return;
      schedule(rel);
      return;
    }
    schedule(null);
  };

  try {
    const w = watch(resolved, { recursive: true }, onEvent);
    attachWatchErrorLog(w);
    return w;
  } catch (e) {
    const code = typeof e === 'object' && e !== null && 'code' in e ? (e as NodeJS.ErrnoException).code : undefined;
    if (code === 'ERR_FEATURE_UNAVAILABLE_ON_PLATFORM') {
      console.warn(
        '[file-index] Recursive file watching is unavailable on this platform; using non-recursive watch (Node 20+ on Linux enables recursive). Nested file changes may be missed until you upgrade.',
      );
      const w = watch(resolved, { recursive: false }, onEvent);
      attachWatchErrorLog(w);
      return w;
    }
    throw e;
  }
}
